import React from "react";
import { useTranslation } from "react-i18next";
import { Info } from "lucide-react";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip";
import { YesNoRadio } from "./YesNoRadio";
import { INFLAMMATION_TESTS } from "./constants";
import { CLINIC_COLORS } from "./theme";

export const AcuteSymptomsSection = ({ questionnaire, updateQuestionnaire, testValues, handleInputChange, hasAcuteSymptoms, isDoctorView }) => {
  const { t } = useTranslation();

  return (
    <>
      <div className="space-y-4">
        <div>
          <h3 className="font-semibold" style={{ color: CLINIC_COLORS.ink }}>{t('dashboard.acuteSymptoms') || 'Acute Symptoms'}</h3>
          {isDoctorView && (
            <p className="text-sm mt-1" style={{ color: CLINIC_COLORS.subtle }}>{t('dashboard.acuteSymptomsSubtitle')}</p>
          )}
        </div>
        
        <YesNoRadio
          id="hasFever"
          label={t('dashboard.hasFever') || 'Do you have fever?'}
          value={questionnaire.hasFever}
          onChange={(v) => updateQuestionnaire('hasFever', v)}
          yesLabel={t('common.yes')}
          noLabel={t('common.no')}
        />
        <YesNoRadio
          id="hasFatigue"
          label={t('dashboard.hasFatigue') || 'Are you experiencing fatigue or weakness?'}
          value={questionnaire.hasFatigue}
          onChange={(v) => updateQuestionnaire('hasFatigue', v)}
          yesLabel={t('common.yes')}
          noLabel={t('common.no')}
        />
        <YesNoRadio
          id="hasNeckPain"
          label={t('dashboard.hasNeckPain') || 'Do you have pain in the front of neck?'}
          value={questionnaire.hasNeckPain}
          onChange={(v) => updateQuestionnaire('hasNeckPain', v)}
          yesLabel={t('common.yes')}
          noLabel={t('common.no')}
        />
        <YesNoRadio
          id="symptomsAfterVirusInfection"
          label={t('dashboard.symptomsAfterVirus') || 'Did symptoms start after a viral infection?'}
          value={questionnaire.symptomsAfterVirusInfection}
          onChange={(v) => updateQuestionnaire('symptomsAfterVirusInfection', v)}
          yesLabel={t('common.yes')}
          noLabel={t('common.no')}
        />
      </div>

      {hasAcuteSymptoms() && (
        <div className="space-y-4 pt-4">
          <h3 className="font-semibold" style={{ color: CLINIC_COLORS.ink }}>{t('dashboard.inflammationMarkers')}</h3>
          <p className="text-sm" style={{ color: CLINIC_COLORS.muted }}>
            {t('dashboard.inflammationMarkersDesc')}
          </p>
          <div className="space-y-4">
            {INFLAMMATION_TESTS.map((test) => (
              <div key={test.key} className="flex items-center gap-4 mb-4">
                <div className="w-28 flex items-center gap-2">
                  <Label htmlFor={`symptoms-${test.key}`} className="font-semibold" style={{ color: CLINIC_COLORS.muted }}>
                    {t(`tests.${test.key}`)}
                  </Label>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button type="button" style={{ color: CLINIC_COLORS.placeholder }}>
                        <Info className="w-4 h-4" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="right" className="max-w-sm">
                      <p className="text-sm">{t(`tests.${test.key}Desc`)}</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
                <div className="flex-1 relative">
                  <Input
                    id={`symptoms-${test.key}`}
                    data-testid={`input-symptoms-${test.key}`}
                    type="text"
                    inputMode="decimal"
                    pattern="[0-9]*[.,]?[0-9]*"
                    placeholder={t('dashboard.recommended') || 'Recommended'}
                    className="pr-20 h-12 text-base"
                    style={{
                      background: "#f1f4f9",
                      border: "2px solid transparent",
                      transition: "border-color 0.2s, background-color 0.2s",
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = CLINIC_COLORS.primary;
                      e.target.style.background = "#ffffff";
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = "transparent";
                      e.target.style.background = "#f1f4f9";
                    }}
                    value={testValues[test.key] || ""}
                    onChange={(e) => handleInputChange(test.key, e.target.value)}
                    onKeyDown={(e) => {
                      const allowedKeys = ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'Home', 'End', '.', ','];
                      if (!allowedKeys.includes(e.key) && !/[0-9]/.test(e.key)) {
                        e.preventDefault();
                      }
                    }}
                  />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm font-medium" style={{ color: CLINIC_COLORS.subtle }}>
                    {test.unit}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
};
